/*
 * File: components/admin/LeaveManager.js
 * Cập nhật: Master-Detail View. Hiển thị danh sách tổng hợp toàn xưởng/bộ phận.
 * Click vào nhân viên sẽ mở Dashboard chi tiết của người đó.
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { 
    apiGetLeaveEntries, 
    apiDeleteLeaveEntry, 
    apiGetEmployeeInfo, 
    apiAdminSubmitLeave, 
    apiFetchHolidays,
    apiUpdateLeaveEntry,
    apiResetEmployeeLeave,
    apiGetGroups,
    apiGetLeaveSummaryByGroup // <-- Đảm bảo API này đã được thêm vào api.js
} from '../../api';

// --- DANH SÁCH CÁC LOẠI PHÉP ---
const LEAVE_TYPE_OPTIONS = { 'E': 'Phép năm', 'A': 'Việc riêng', 'B': 'Phép Bệnh', 'C': 'Nghỉ kết hôn', 'D': 'Phép Tang', 'F': 'Nghỉ thai sản', 'G': 'Nghỉ công tác', 'H': 'Nghỉ C.Thường', 'I': 'Đi đường', 'K': 'Không lương' };

const getYearFromNote = (note) => {
    if (!note) return null;
    const match = note.toString().match(/(20\d{2})/);
    return match ? match[1] : null;
};

// --- MODALS ---
const EditModal = ({ entry, onSave, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
        DateUP: new Date(entry.DateUP).toISOString().split('T')[0], 
        TimeUP: entry.TimeUP.replace(':', ''),
        TimeDown: entry.TimeDown.replace(':', ''),
        JiaType: entry.JiaType.trim(),
        memo: entry.memo || ''
    });

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            TimeUP: `${formData.TimeUP.substring(0, 2)}:${formData.TimeUP.substring(2, 4)}`,
            TimeDown: `${formData.TimeDown.substring(0, 2)}:${formData.TimeDown.substring(2, 4)}`
        };
        onSave(entry.id, payload);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
                <h2 className="text-xl font-bold mb-4">Chỉnh sửa ngày phép</h2>
                <div>
                    <label className="block text-sm font-medium">Ngày nghỉ</label>
                    <input type="date" name="DateUP" value={formData.DateUP} onChange={handleChange} className="mt-1 w-full px-3 py-2 border rounded-md" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium">Giờ bắt đầu (hhmm)</label>
                        <input type="text" name="TimeUP" value={formData.TimeUP} onChange={handleChange} maxLength={4} className="mt-1 w-full px-3 py-2 border rounded-md" required />
                    </div>
                    <div>
                        <label className="block text-sm font-medium">Giờ kết thúc (hhmm)</label>
                        <input type="text" name="TimeDown" value={formData.TimeDown} onChange={handleChange} maxLength={4} className="mt-1 w-full px-3 py-2 border rounded-md" required />
                    </div>
                </div>
                <div>
                   <label className="block text-sm font-medium">Loại phép</label>
                    <select name="JiaType" value={formData.JiaType} onChange={handleChange} className="mt-1 w-full px-3 py-2 border rounded-md bg-white">
                        {Object.entries(LEAVE_TYPE_OPTIONS).map(([key, value]) => ( <option key={key} value={key}>{key}: {value}</option> ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium">Lý do (nếu có)</label>
                    <textarea name="memo" value={formData.memo} onChange={handleChange} rows="2" className="mt-1 w-full px-3 py-2 border rounded-md"/>
                </div>
                <div className="flex justify-end gap-4 pt-4">
                    <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 rounded-md">Hủy</button>
                    <button type="submit" disabled={isLoading} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:bg-blue-400">
                        {isLoading ? 'Đang lưu...' : 'Lưu thay đổi'}
                    </button>
                </div>
            </form>
        </div>
    );
};

const ConfirmationModal = ({ title, message, onConfirm, onCancel, confirmText = "Xác nhận", confirmColor = "red" }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold mb-4">{title}</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="flex justify-end gap-4">
                <button onClick={onCancel} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Hủy</button>
                <button onClick={onConfirm} className={`px-4 py-2 text-white rounded-md bg-${confirmColor}-600 hover:bg-${confirmColor}-700`}>
                    {confirmText}
                </button>
            </div>
        </div>
    </div>
);

const AlertModal = ({ title, message, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold text-red-600 mb-4">{title}</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="flex justify-end">
                <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">Đã hiểu</button>
            </div>
        </div>
    </div>
);

// --- HÀM TIỆN ÍCH ---
const calculateHours = (startStr, endStr) => {
    if (!startStr || !endStr || startStr.length < 4 || endStr.length < 4) return 0;
    const startH = parseInt(startStr.substring(0, 2)), startM = parseInt(startStr.substring(2, 4));
    const endH = parseInt(endStr.substring(0, 2)), endM = parseInt(endStr.substring(2, 4));
    if (isNaN(startH) || isNaN(startM) || isNaN(endH) || isNaN(endM)) return 0;
    
    let totalMinutes = 0;
    const startTotalMinutes = startH * 60 + startM;
    const endTotalMinutes = endH * 60 + endM;

    const morningStart = 8 * 60, morningEnd = 12 * 60;
    const afternoonStart = 13 * 60, afternoonEnd = 17 * 60;

    const morningOverlapStart = Math.max(startTotalMinutes, morningStart);
    const morningOverlapEnd = Math.min(endTotalMinutes, morningEnd);
    if (morningOverlapEnd > morningOverlapStart) totalMinutes += morningOverlapEnd - morningOverlapStart;

    const afternoonOverlapStart = Math.max(startTotalMinutes, afternoonStart);
    const afternoonOverlapEnd = Math.min(endTotalMinutes, afternoonEnd);
    if (afternoonOverlapEnd > afternoonOverlapStart) totalMinutes += afternoonOverlapEnd - afternoonOverlapStart;

    const roundedMinutes = Math.floor(totalMinutes / 30) * 30;
    return parseFloat((roundedMinutes / 60).toFixed(1));
};

const parseDateForFrontend = (ddmmyyyy) => {
    if (!ddmmyyyy || ddmmyyyy.length !== 8) return null;
    const day = parseInt(ddmmyyyy.substring(0, 2));
    const month = parseInt(ddmmyyyy.substring(2, 4)) - 1;
    const year = parseInt(ddmmyyyy.substring(4, 8));
    const date = new Date(year, month, day);
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
    return date;
};

const PUBLIC_HOLIDAYS = {
    '2025-01-01': 'Tết Dương lịch', '2025-01-28': 'Tết Nguyên Đán', '2025-01-29': 'Tết Nguyên Đán',
    '2025-01-30': 'Tết Nguyên Đán', '2025-01-31': 'Tết Nguyên Đán', '2025-02-03': 'Nghỉ bù Tết Nguyên Đán',
    '2025-04-08': 'Giỗ Tổ Hùng Vương', '2025-04-30': 'Ngày Giải phóng miền Nam', '2025-05-01': 'Ngày Quốc tế Lao động',
    '2025-05-02': 'Nghỉ bù', '2025-09-01': 'Nghỉ Quốc Khánh', '2025-09-02': 'Quốc Khánh',
};

export default function LeaveManager() {
    const [viewMode, setViewMode] = useState('list'); // 'list' hoặc 'detail'
    const [year, setYear] = useState(new Date().getFullYear());
    
    // State cho List View
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('ALL');
    const [summaryList, setSummaryList] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // State cho Detail View
    const [employeeId, setEmployeeId] = useState('');
    const [employeeInfo, setEmployeeInfo] = useState(null);
    const [leaveEntries, setLeaveEntries] = useState([]);
    const [formState, setFormState] = useState({ startDate: '', endDate: '', leaveType: 'E', startTime: '', endTime: '', reason: '' });
    const [dailyBreakdown, setDailyBreakdown] = useState([]);
    const [resetDate, setResetDate] = useState(new Date().toISOString().split('T')[0]);

    // Trạng thái chung
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [modalState, setModalState] = useState({ type: null, data: null });

    // Load danh sách bộ phận
    useEffect(() => {
        apiGetGroups().then(setGroups).catch(console.error);
    }, []);

    // Load bảng tổng hợp khi đổi bộ phận hoặc đổi năm
    useEffect(() => {
        if (viewMode === 'list') {
            const fetchSummary = async () => {
                setIsLoading(true);
                try {
                    const data = await apiGetLeaveSummaryByGroup(year, selectedGroup);
                    setSummaryList(data);
                } catch (error) {
    setMessage({ type: 'error', text: `Lỗi: ${error.message}` });
} finally {
                    setIsLoading(false);
                }
            };
            fetchSummary();
        }
    }, [year, selectedGroup, viewMode]);

    // Chuyển sang màn hình chi tiết
    const openDetail = async (empid) => {
        setEmployeeId(empid);
        setViewMode('detail');
        await fetchEmployeeDetail(empid);
    };

    const fetchEmployeeDetail = async (empid) => {
        setIsLoading(true);
        setMessage({ type: '', text: '' });
        setLeaveEntries([]);
        setEmployeeInfo(null);
        try {
            const [empInfo, entriesData, summaryRes] = await Promise.all([
                apiGetEmployeeInfo(empid),
                apiGetLeaveEntries(empid, year),
                apiFetchHolidays(empid, year)
            ]);
            setEmployeeInfo({ name: empInfo.EMPNAM_VN, department: empInfo.department, ...summaryRes.summary });
            setLeaveEntries(entriesData);
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setIsLoading(false);
        }
    };

    // TÍNH TOÁN SỐ PHÉP TỒN CÒN LẠI
    const currentCarriedOver = useMemo(() => {
        if (!employeeInfo || !employeeInfo.carriedOverHours) return 0;
        const initialCarriedOver = employeeInfo.carriedOverHours;
        const usedCarriedOver = leaveEntries.reduce((total, row) => {
            if (!row.JiaType || row.JiaType.trim() !== 'E') return total;
            const note = (row.memo || '').toString();
            if (getYearFromNote(note)) {
                return total + (parseFloat(row.HHour) || 0);
            }
            return total;
        }, 0);
        return Math.max(0, initialCarriedOver - usedCarriedOver);
    }, [employeeInfo, leaveEntries]);

    // THỐNG KÊ CHI TIẾT TỪNG LOẠI PHÉP ĐÃ NGHỈ
    const leaveBreakdown = useMemo(() => {
        if (!leaveEntries || leaveEntries.length === 0) return {};
        const breakdown = {};
        leaveEntries.forEach(entry => {
            const type = entry.JiaTypeName || entry.JiaType;
            const hours = parseFloat(entry.HHour) || 0;
            if (!breakdown[type]) breakdown[type] = 0;
            breakdown[type] += hours;
        });
        return breakdown;
    }, [leaveEntries]);

    const handleSubmitLeave = async (e) => {
        e.preventDefault();
        const proceedWithSubmit = async () => {
            setIsLoading(true);
            try {
                const leaveData = { userId: employeeId, ...formState, endDate: formState.endDate || formState.startDate };
                await apiAdminSubmitLeave(leaveData);
                setMessage({ type: 'success', text: 'Nhập phép thành công!' });
                setFormState({ startDate: '', endDate: '', leaveType: 'E', startTime: '', endTime: '', reason: '' });
                await fetchEmployeeDetail(employeeId);
            } catch (error) {
                setMessage({ type: 'error', text: `Lỗi khi nhập phép: ${error.message}` });
            } finally {
                setIsLoading(false);
            }
        };

        const remainingLeave = employeeInfo?.remaining ?? 0;
        if (formState.leaveType === 'E' && totalHours > remainingLeave) {
            const advanceAmount = totalHours - remainingLeave;
            if (advanceAmount > 8) {
                setModalState({ type: 'alert', data: { title: 'Không hợp lệ', message: `Không được ứng phép quá 8 giờ.` }});
                return; 
            }
            setModalState({
                type: 'confirmAdvance',
                data: {
                    title: 'Xác nhận Ứng Phép',
                    message: `Nhân viên này không đủ phép. Bạn có muốn ứng trước ${advanceAmount.toFixed(1)} giờ phép không?`,
                    onConfirm: () => { setModalState({ type: null, data: null }); proceedWithSubmit(); }
                }
            });
        } else {
            proceedWithSubmit();
        }
    };
    
    const handleSaveEdit = async (id, updatedData) => {
        setIsLoading(true);
        try {
            await apiUpdateLeaveEntry(id, updatedData);
            setMessage({ type: 'success', text: 'Cập nhật ngày phép thành công!' });
            setModalState({ type: null, data: null });
            await fetchEmployeeDetail(employeeId);
        } catch (error) {
            setMessage({ type: 'error', text: `Lỗi khi cập nhật: ${error.message}` });
        } finally {
            setIsLoading(false);
        }
    };
    
    const confirmDelete = async () => {
        if (!modalState.data) return;
        setIsLoading(true);
        try {
            await apiDeleteLeaveEntry(modalState.data.id);
            setMessage({ type: 'success', text: 'Xóa ngày phép thành công!' });
            await fetchEmployeeDetail(employeeId);
        } catch (error) {
            setMessage({ type: 'error', text: `Lỗi khi xóa: ${error.message}` });
        } finally {
            setIsLoading(false);
            setModalState({ type: null, data: null });
        }
    };

    const confirmResetLeave = async () => {
        if (!modalState.data || !modalState.data.date) return;
        setIsLoading(true);
        try {
            const payload = { userId: employeeId, year: new Date(modalState.data.date).getFullYear(), adjustmentDate: modalState.data.date };
            const result = await apiResetEmployeeLeave(payload);
            setMessage({ type: 'success', text: result.message });
            await fetchEmployeeDetail(employeeId);
        } catch (error) {
            setMessage({ type: 'error', text: `Lỗi khi chốt sổ: ${error.message}` });
        } finally {
            setIsLoading(false);
            setModalState({ type: null, data: null });
        }
    };

    const handleFormChange = (e) => setFormState(prev => ({ ...prev, [e.target.name]: e.target.value }));

    useEffect(() => {
        const { startDate, endDate, startTime, endTime } = formState;
        const sDate = parseDateForFrontend(startDate);
        const eDate = parseDateForFrontend(endDate || startDate);
        if (!sDate || !eDate || eDate < sDate || !startTime || !endTime) {
            setDailyBreakdown([]); return;
        }
        let breakdown = [];
        let currentDate = new Date(sDate);
        while(currentDate <= eDate) {
            const dayOfWeek = currentDate.getDay();
            const dateString = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
            let hours = 0, note = '';
            if (dayOfWeek === 0) { note = 'Chủ Nhật'; } 
            else if (PUBLIC_HOLIDAYS[dateString]) { note = PUBLIC_HOLIDAYS[dateString]; } 
            else {
                const isSameDayPeriod = sDate.getTime() === eDate.getTime();
                const isFirstDay = currentDate.getTime() === sDate.getTime();
                const isLastDay = currentDate.getTime() === eDate.getTime();
                if (isSameDayPeriod) { hours = calculateHours(startTime, endTime); }
                else if (isFirstDay) { hours = calculateHours(startTime, '1700'); }
                else if (isLastDay) { hours = calculateHours('0800', endTime); }
                else { hours = 8; }
            }
            breakdown.push({ date: currentDate.toLocaleDateString('vi-VN'), hours, note });
            currentDate.setDate(currentDate.getDate() + 1);
        }
        setDailyBreakdown(breakdown);
    }, [formState.startDate, formState.endDate, formState.startTime, formState.endTime]);

    const totalHours = dailyBreakdown.reduce((acc, day) => acc + day.hours, 0);
    const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + 1 - i);

    // Lọc danh sách hiển thị
    const filteredSummaryList = summaryList.filter(emp => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (emp.empid.toLowerCase().includes(term) || (emp.empName && emp.empName.toLowerCase().includes(term)));
    });

    return (
        <div>
            {modalState.type === 'edit' && <EditModal entry={modalState.data} onSave={handleSaveEdit} onCancel={() => setModalState({ type: null, data: null })} isLoading={isLoading} />}
            {modalState.type === 'delete' && <ConfirmationModal title="Xác nhận xóa" message={`Bạn có chắc muốn xóa ngày phép ${new Date(modalState.data.DateUP).toLocaleDateString('vi-VN')}?`} onConfirm={confirmDelete} onCancel={() => setModalState({ type: null, data: null })} confirmText="Xác nhận Xóa" />}
            {modalState.type === 'reset' && <ConfirmationModal title="Xác nhận chốt sổ" message={`Bạn có chắc muốn chốt sổ phép cho nhân viên ${employeeId} tại ngày ${new Date(modalState.data.date).toLocaleDateString('vi-VN')}?`} onConfirm={confirmResetLeave} onCancel={() => setModalState({ type: null, data: null })} confirmText="Đồng ý Chốt sổ" confirmColor="orange" />}
            {modalState.type === 'confirmAdvance' && <ConfirmationModal title={modalState.data.title} message={modalState.data.message} onConfirm={modalState.data.onConfirm} onCancel={() => setModalState({ type: null, data: null })} confirmText="Đồng ý Ứng" confirmColor="orange" />}
            {modalState.type === 'alert' && <AlertModal title={modalState.data.title} message={modalState.data.message} onClose={() => setModalState({ type: null, data: null })} />}

            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">Quản lý Phép Nhân viên</h2>
                {viewMode === 'detail' && (
                    <button onClick={() => setViewMode('list')} className="px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded hover:bg-gray-300 flex items-center transition-colors">
                        <span className="mr-2">←</span> Quay lại danh sách
                    </button>
                )}
            </div>
            
            {message.text && ( <p className={`text-center p-3 rounded-md mb-4 text-sm ${ message.type === 'error' ? 'bg-red-100 text-red-700' : (message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700') }`}> {message.text} </p> )}
            
            {/* VIEW 1: BẢNG TỔNG HỢP DANH SÁCH */}
            {viewMode === 'list' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-col md:flex-row gap-4 items-center">
                        <div className="flex-1 w-full">
                            <input 
                                type="text" 
                                placeholder="Tìm mã hoặc tên nhân viên..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div className="w-full md:w-64">
                            <select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)} className="w-full px-4 py-2 border rounded-md bg-white">
                                <option value="ALL">-- Tất cả bộ phận --</option>
                                {groups.map(g => <option key={g.groupId} value={g.groupId}>{g.groupName}</option>)}
                            </select>
                        </div>
                        <div className="w-full md:w-32">
                            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="w-full px-4 py-2 border rounded-md bg-white">
                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="overflow-x-auto max-h-[600px] overflow-y-auto relative">
                        <table className="min-w-full text-left bg-white">
                            <thead className="bg-gray-100 sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="px-4 py-3 font-semibold text-gray-700 text-sm">Mã NV</th>
                                    <th className="px-4 py-3 font-semibold text-gray-700 text-sm">Tên Nhân Viên</th>
                                    <th className="px-4 py-3 font-semibold text-gray-700 text-sm">Bộ phận</th>
                                    <th className="px-4 py-3 font-semibold text-gray-700 text-sm text-center">Tổng phép</th>
                                    <th className="px-4 py-3 font-semibold text-gray-700 text-sm text-center">Đã nghỉ</th>
                                    <th className="px-4 py-3 font-semibold text-blue-700 text-sm text-center">Còn lại</th>
                                    <th className="px-4 py-3 font-semibold text-gray-700 text-sm text-center">Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {isLoading ? (
                                    <tr><td colSpan="7" className="text-center py-8 text-gray-500">Đang tải dữ liệu...</td></tr>
                                ) : filteredSummaryList.length === 0 ? (
                                    <tr><td colSpan="7" className="text-center py-8 text-gray-500">Không có dữ liệu nhân viên.</td></tr>
                                ) : (
                                    filteredSummaryList.map(emp => (
                                        <tr key={emp.empid} className="hover:bg-blue-50 transition-colors">
                                            <td className="px-4 py-3 font-medium text-gray-900">{emp.empid}</td>
                                            <td className="px-4 py-3 text-gray-700 font-semibold">{emp.empName}</td>
                                            <td className="px-4 py-3 text-gray-600 text-sm">{emp.groupName}</td>
                                            <td className="px-4 py-3 text-center text-gray-700">{emp.total}</td>
                                            <td className="px-4 py-3 text-center text-red-600 font-medium">{emp.used}</td>
                                            <td className="px-4 py-3 text-center text-blue-700 font-bold text-lg">{emp.remaining}</td>
                                            <td className="px-4 py-3 text-center">
                                                <button onClick={() => openDetail(emp.empid)} className="px-3 py-1.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded text-sm font-semibold transition-colors">
                                                    Quản lý
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* VIEW 2: CHI TIẾT TỪNG NHÂN VIÊN */}
            {viewMode === 'detail' && employeeInfo && (
                <div className="space-y-8 animate-fade-in-up">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 border-b pb-4 gap-4">
                            <div>
                                <h3 className="text-2xl font-bold text-gray-800">{employeeInfo.name}</h3>
                                <p className="text-gray-500 font-medium">Mã NV: {employeeId} | Bộ phận: {employeeInfo.department || 'Chưa cập nhật'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="date" value={resetDate} onChange={(e) => setResetDate(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                                <button onClick={() => setModalState({ type: 'reset', data: { date: resetDate } })} disabled={isLoading || !resetDate} className="px-4 py-2 text-sm font-bold text-white bg-orange-500 rounded-md hover:bg-orange-600 disabled:bg-gray-400 shadow-sm" title="Chốt số phép tính đến ngày này">
                                    Chốt Sổ Phép
                                </button>
                            </div>
                        </div>

                        {/* DASHBOARD THỐNG KÊ */}
                        <div className="mb-8">
                            <h4 className="text-sm font-bold text-gray-700 uppercase mb-3">Thống kê nghỉ phép năm {year}</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4">
                                <div className="bg-green-50 p-4 rounded-xl border border-green-200 shadow-sm">
                                    <p className="text-xs text-green-700 font-bold uppercase mb-1">Phép năm CÒN LẠI</p>
                                    <p className="text-3xl font-black text-green-600">{employeeInfo.remaining} <span className="text-sm font-medium text-green-700">giờ</span></p>
                                    {employeeInfo.carriedOverHours > 0 && (
                                        <p className="text-xs text-orange-600 font-medium mt-1">(Trong đó: {parseFloat(currentCarriedOver).toString()} giờ phép tồn)</p>
                                    )}
                                </div>
                                {Object.keys(leaveBreakdown).length > 0 ? (
                                    Object.entries(leaveBreakdown).map(([type, hours]) => (
                                        <div key={type} className="bg-gray-50 p-4 rounded-xl border border-gray-200 shadow-sm">
                                            <p className="text-xs text-gray-500 font-bold uppercase mb-1">Đã nghỉ: {type}</p>
                                            <p className="text-2xl font-bold text-gray-800">{hours} <span className="text-sm font-medium text-gray-500">giờ</span></p>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-span-3 flex items-center justify-center bg-gray-50 rounded-xl border border-dashed border-gray-300 p-4">
                                        <p className="text-sm text-gray-400 italic">Chưa có dữ liệu nghỉ phép nào trong năm {year}</p>
                                    </div>
                                )}
                            </div>
                            {employeeInfo.source === 'Adjustment Record' && (
                                <p className="text-xs text-gray-500 italic mt-3">* Dữ liệu phép năm được tính từ lần chốt sổ ngày {new Date(employeeInfo.adjustmentDate).toLocaleDateString('vi-VN')}</p>
                            )}
                        </div>

                        {/* FORM NHẬP PHÉP MỚI */}
                        <div className="border-t pt-6 mt-6">
                            <h3 className="text-lg font-bold text-gray-800 mb-4">Thêm Ngày Nghỉ Mới</h3>
                            <form onSubmit={handleSubmitLeave} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium">Từ ngày (ddmmyyyy)</label>
                                        <input type="text" name="startDate" value={formState.startDate} onChange={handleFormChange} className="mt-1 w-full px-3 py-2 border rounded-md" maxLength={8} required />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium">Đến ngày (ddmmyyyy)</label>
                                        <input type="text" name="endDate" value={formState.endDate} onChange={handleFormChange} className="mt-1 w-full px-3 py-2 border rounded-md" maxLength={8} placeholder="Bỏ trống nếu nghỉ 1 ngày" />
                                    </div>
                                </div>
                                <div>
                                <label className="block text-sm font-medium">Loại phép</label>
                                    <select name="leaveType" value={formState.leaveType} onChange={handleFormChange} className="mt-1 w-full px-3 py-2 border rounded-md bg-white">
                                        {Object.entries(LEAVE_TYPE_OPTIONS).map(([key, value]) => (<option key={key} value={key}>{key}: {value}</option>))}
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium">Giờ bắt đầu (hhmm)</label>
                                        <input type="text" name="startTime" value={formState.startTime} onChange={handleFormChange} maxLength={4} required className="mt-1 w-full px-3 py-2 border rounded-md" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium">Giờ kết thúc (hhmm)</label>
                                        <input type="text" name="endTime" value={formState.endTime} onChange={handleFormChange} maxLength={4} required className="mt-1 w-full px-3 py-2 border rounded-md" />
                                    </div>
                                </div>
                                {dailyBreakdown.length > 0 && (
                                    <div className="text-sm bg-indigo-50 p-3 rounded-md">
                                        <h4 className="font-bold text-gray-700 mb-2">Chi tiết giờ nghỉ:</h4>
                                        <ul className="list-disc list-inside space-y-1">
                                            {dailyBreakdown.map((day, index) => (
                                                <li key={index} className={`text-gray-800 ${day.note ? 'text-gray-500' : ''}`}>
                                                    Ngày {day.date}: {day.note ? ( <span className="font-semibold">{day.note} - 0 giờ</span> ) : ( <span className="font-semibold">{day.hours} giờ</span> )}
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="border-t border-indigo-200 mt-2 pt-2">
                                            <p className="font-bold text-indigo-700">Tổng cộng giờ phép tính: {totalHours} giờ</p>
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium">Lý do (nếu có)</label>
                                    <textarea name="reason" value={formState.reason} onChange={handleFormChange} rows="2" className="mt-1 w-full px-3 py-2 border rounded-md" />
                                </div>
                                <button type="submit" disabled={isLoading || totalHours <= 0} className="w-full px-4 py-3 font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 transition-colors">
                                    {isLoading ? 'Đang lưu...' : `Lưu và Nhập Phép (${totalHours} giờ)`}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* BẢNG LỊCH SỬ NGHỈ PHÉP CÁ NHÂN */}
                    {leaveEntries.length > 0 && (
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Lịch sử nghỉ phép chi tiết</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ngày nghỉ</th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Loại phép</th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Số giờ</th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Ghi chú</th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Hành động</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {leaveEntries.map(entry => {
                                            const yearInNote = getYearFromNote(entry.memo);
                                            return (
                                                <tr key={entry.id} className="hover:bg-blue-50 transition-colors">
                                                    <td className="px-4 py-3 font-medium text-gray-900">{new Date(entry.DateUP).toLocaleDateString('vi-VN')}</td>
                                                    <td className="px-4 py-3 text-gray-700">{entry.JiaTypeName}</td>
                                                    <td className="px-4 py-3 font-bold text-gray-900">{entry.HHour}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">
                                                        {entry.memo}
                                                        {yearInNote && ( <span className="ml-2 px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-xs font-bold border border-orange-200">(Tồn {yearInNote})</span> )}
                                                    </td>
                                                    <td className="px-4 py-3 space-x-2">
                                                        <button onClick={() => setModalState({ type: 'edit', data: entry })} disabled={isLoading} className="px-3 py-1.5 text-sm font-semibold text-blue-600 bg-blue-100 rounded hover:bg-blue-200 transition-colors">Sửa</button>
                                                        <button onClick={() => setModalState({ type: 'delete', data: entry })} disabled={isLoading} className="px-3 py-1.5 text-sm font-semibold text-red-600 bg-red-100 rounded hover:bg-red-200 transition-colors">Xóa</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}